import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  AppDispatch,
  getReportList,
  getReportListClear,
  getReportListRequest,
  getReportOptions,
  getReportOptionsRequest,
} from "@src/redux";
import * as TYPES from "@src/redux/types";
import ReportFilterBar from "../components/report/ReportFilterBar";
import ReportTable from "../components/report/ReportTable";
import ReportExportModal from "../components/report/ReportExportModal";
import ReportExclusionNote from "../components/report/ReportExclusionNote";

const DEFAULT_PAGE_LIMIT = 10;

/**
 * Report page: Date Range + Root Level filters over the reading table, with an
 * Export action that opens the column-picker popup.
 *
 * The preview table and the export share one filter object, so the sheet the
 * user downloads covers exactly the rows they are looking at. The preview
 * renders the default column plan (Name, Age and every question); the popup's
 * ticks decide the columns of the export itself.
 */
const Report: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const { result: options, error: optionsError } =
    useSelector(getReportOptions);
  const { result: reportResult, loading, error } = useSelector(getReportList);

  // The no-finding exclusion is on by default: subjects and question columns
  // whose answers are all blank or "Not at all" are left out. It can empty the
  // table for a client with no positive findings, which is why
  // ReportExclusionNote always states what was removed and offers "Show all" —
  // an empty grid with no explanation is what made this page look broken
  // before.
  const [filter, setFilter] = useState<TYPES.ReportFilterInput>({
    excludeEmptyAnswers: true,
    readingScope: "all",
  });
  const [pagination, setPagination] = useState<TYPES.PaginationInput>({
    pageNumber: 1,
    pageLimit: DEFAULT_PAGE_LIMIT,
  });
  const [isExportModalVisible, setIsExportModalVisible] =
    useState<boolean>(false);

  const loadReport = useCallback(
    (
      nextFilter: TYPES.ReportFilterInput,
      nextPagination: TYPES.PaginationInput
    ) => {
      dispatch(
        getReportListRequest({
          filter: nextFilter,
          pagination: nextPagination,
        })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(getReportOptionsRequest({}));
    return () => {
      dispatch(getReportListClear());
    };
  }, [dispatch]);

  // First load, and every reload after a filter change, starts at page 1.
  useEffect(() => {
    loadReport(filter, pagination);
  }, [filter, pagination, loadReport]);

  useEffect(() => {
    const message = error?.message || optionsError?.message;
    if (message) toast.error(message);
  }, [error, optionsError]);

  const handleRootLevelChange = (rootLevelOrganizationId?: string) => {
    // Clear the drill-down: a City from the previous client is meaningless
    // under the new one, and leaving it set would scope the report to an
    // organization outside the selected tree.
    setFilter((prev) => ({
      ...prev,
      rootLevelOrganizationId,
      organizationId: undefined,
    }));
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
    // The question and subject-key catalogue is per client, so the popup
    // options have to be refetched when the Root Level changes.
    dispatch(getReportOptionsRequest({ filter: { rootLevelOrganizationId } }));
  };

  const handleDateRangeChange = (fromDate?: string, toDate?: string) => {
    setFilter((prev) => ({ ...prev, fromDate, toDate }));
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  };

  const handleExcludeEmptyAnswersChange = (excludeEmptyAnswers: boolean) => {
    setFilter((prev) => ({ ...prev, excludeEmptyAnswers }));
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  };

  const handleReadingScopeChange = (
    readingScope: TYPES.ReportReadingScope
  ) => {
    setFilter((prev) => ({ ...prev, readingScope }));
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  };

  const handleOrganizationChange = (organizationId?: string) => {
    setFilter((prev) => ({ ...prev, organizationId }));
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  };

  const handlePageChange = (pageNumber: number, pageLimit: number) => {
    setPagination({ pageNumber, pageLimit });
  };


  return (
    <div className="report-page">
      <ReportFilterBar
        rootLevelList={options?.rootLevelList ?? []}
        rootLevelOrganizationId={filter.rootLevelOrganizationId}
        fromDate={filter.fromDate as string}
        toDate={filter.toDate as string}
        excludeEmptyAnswers={filter.excludeEmptyAnswers ?? true}
        readingScope={filter.readingScope ?? "all"}
        onRootLevelChange={handleRootLevelChange}
        onDateRangeChange={handleDateRangeChange}
        onExcludeEmptyAnswersChange={handleExcludeEmptyAnswersChange}
        onReadingScopeChange={handleReadingScopeChange}
        onOrganizationChange={handleOrganizationChange}
        onExportClick={() => setIsExportModalVisible(true)}
        exportDisabled={loading}
      />

      <ReportExclusionNote
        exclusion={reportResult?.exclusion}
        isEmptyResult={(reportResult?.items?.length ?? 0) === 0 && !loading}
        onShowAll={() => handleExcludeEmptyAnswersChange(false)}
      />

      <ReportTable
        result={reportResult}
        loading={loading}
        onPageChange={handlePageChange}
      />

      <ReportExportModal
        isModalVisible={isExportModalVisible}
        setIsModalVisible={setIsExportModalVisible}
        filter={filter}
      />
    </div>
  );
};

export default Report;
